import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidResponsesQuery,
  DeleteBidResponsesMutation,
  DeleteBidResponsesMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { updateBidQuery } from "../bids/updateBidCache";

const DELETE_BID_RESPONSE = graphql(`
  mutation DeleteBidResponses($ids: [String!]!) {
    deleteBidResponses(ids: $ids) {
      id
      bidId
      reference
      title
      section
      description
      weighting
      formattingRequirements
      response
      teamId
      companyId
      stageId
      assignee {
        guid
        firstName
        lastName
      }
      reviewer {
        guid
        firstName
        lastName
      }
      dueDate
      createdAt
      updatedAt
      deletedAt
    }
  }
`);

export function useDeleteBidResponses(
  options?: UseMutationOptions<
    DeleteBidResponsesMutation,
    ClientError,
    DeleteBidResponsesMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const { data, ...rest } = useGraphQlMutation(DELETE_BID_RESPONSE, {
    ...options,
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);

      const idsToDelete = new Set(data.deleteBidResponses.map((r) => r.id));

      // Remove per-response detail caches.
      for (const id of idsToDelete) {
        queryClient.removeQueries({
          queryKey: ["bidResponse", id],
        });
      }

      // Remove deleted responses from any cached bidResponses lists (regardless of scope/pagination/filters).
      queryClient.setQueriesData<BidResponsesQuery>(
        { queryKey: ["bidResponses"], exact: false },
        (oldData) => {
          if (!oldData?.bidResponses) return oldData;

          const filtered = oldData.bidResponses.items.filter((r) => !idsToDelete.has(r.id));
          return {
            ...oldData,
            bidResponses: {
              ...oldData.bidResponses,
              items: filtered,
              totalCount: filtered.length,
            },
          };
        },
      );

      // Keep bid detail caches in sync.
      const deletedByBid = new Map<string, Set<string>>();

      for (const resp of data.deleteBidResponses) {
        if (resp.bidId) {
          const set = deletedByBid.get(resp.bidId) ?? new Set<string>();
          set.add(resp.id);
          deletedByBid.set(resp.bidId, set);
        }
      }

      for (const [bidId, ids] of deletedByBid.entries()) {
        updateBidQuery(queryClient, bidId, (bid) => {
          if (!bid) return bid;

          const updatedbidResponses = bid.bidResponses?.filter((r) => !ids.has(r.id));
          return {
            ...bid,
            bidResponses: updatedbidResponses,
          };
        });
      }
    },
  });

  return { data: data?.deleteBidResponses, ...rest };
}
